import React, { useState, useEffect } from 'react';
import { Paper, Typography, Tabs, Tab, IconButton, Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility'; // Eye icon for view details
import CloseIcon from '@mui/icons-material/Close'; // X icon for clear
import { toast } from 'react-toastify';

const getQuotedRequests = async (token, agentID) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(`${baseURL}PostingTransferBond/${agentID}/QuotedRequests`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching quoted requests: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching quoted requests:', error);
    throw error;
  }
};

const QuoteGrids = ({ selectedAgency, refreshToggle, refreshHandler, canViewRequestingAgency }) => {
  const [activeTabQuotes, setActiveTabQuotes] = useState(0);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [declinedQuotes, setDeclinedQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQuotedRequests = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token || !selectedAgency) {
      toast.error('No authentication token or selected agency found.');
      setLoading(false);
      return;
    }
    try {
      const data = await getQuotedRequests(token, selectedAgency.id);
      const pending = data.filter((quote) => !quote.hasBeenDeclined && quote.statusCd === "O")
      .map((quote) => ({
        ...quote,
        id: quote.transferBondTransactionID,
        defendantName: `${quote.defendantFirstName?.trim() || ''} ${quote.defendantLastName?.trim() || ''}`.trim() || 'N/A',
      }));
      const declined = data.filter((quote) => quote.hasBeenDeclined && quote.userCleared !== true)
      .map((quote) => ({
        ...quote,
        id: quote.transferBondTransactionID,
        defendantName: `${quote.defendantFirstName?.trim() || ''} ${quote.defendantLastName?.trim() || ''}`.trim() || 'N/A',
      }));
      setPendingQuotes(pending);
      setDeclinedQuotes(declined);
    } catch (error) {
      toast.error('Error fetching quoted requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotedRequests();
  }, [selectedAgency, refreshToggle]);

  const handleTabChangeQuotes = (event, newValue) => {
    setActiveTabQuotes(newValue);
  };

  const handleClear = async (transactionId) => {
    var agentId = selectedAgency.id;
    var token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    try {
      const response = await fetch(`${baseURL}PostingTransferBond/${transactionId}/clear/${agentId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (!response.ok) {
        throw new Error(`Unable to Clear Quote`);
      }
      refreshHandler();
      toast.success('Quote Cleared Successfully');
    } catch (error) {
      console.error('Unable to Clear Quote:', error);
      throw error;
    }
  };

  const handleCancel = async (transactionId) => {
    var agentId = selectedAgency.id;
    var token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    try {
      const response = await fetch(`${baseURL}PostingTransferBond/${transactionId}/cancelPending/${agentId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (!response.ok) {
        throw new Error(`Unable to Clear Quote`);
      }
      refreshHandler();
      toast.success('Quote Cleared Successfully');
    } catch (error) {
      console.error('Unable to Clear Quote:', error);
      throw error;
    }
  };

  // Define total fee calculation
  const calculateTotalFees = (row) => {
    const executionFee = row?.executionFee || 0;
    const travelFee = row?.travelFee || 0;
    const otherFee = row?.otherFee || 0;
    return executionFee + travelFee + otherFee;
  };

  const convertMinutesToHours = (minutes) => {
    return (minutes / 60).toFixed(2);
  };

  const calculateCountdown = (expireDate) => {
    const expirationTime = new Date(expireDate).getTime();
    const now = new Date().getTime();
    const timeDiff = expirationTime - now;

    if (timeDiff <= 0) return 'Expired';

    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  function formatDate(row) {
    const date = new Date(row.dateRecorded);
    // debugger;
    // Check if the date is valid
    if (isNaN(date.getTime())) return "Invalid Date";
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    
    // Return formatted as MM/DD/YYYY
    return `${month}/${day}/${year}`;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setPendingQuotes((prevQuotes) => [...prevQuotes]);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const quoteColumns = [
    { field: 'transferBondTransactionID', headerName: 'Ref #', width: 100 },
    { field: 'defendantName', headerName: 'Defendant Name', width: 200 },
    ...(canViewRequestingAgency
      ? [
          {
            field: 'requestingAgencyName',
            headerName: 'Requesting Agency',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  width: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                }}
              >
                {params.row.requestingAgencyName || '-'}
              </Box>
            ),
          },
        ]
      : []),
    { field: 'county', headerName: 'County', width: 100 },
    { field: 'state', headerName: 'State', width: 65 },
    { field: 'jailLocation', headerName: 'Jail', width: 100, valueGetter: (params) => params || '-' },
    { field: 'courtPlace', headerName: 'Courthouse', width: 100, valueGetter: (params) => params || '-' },
    { field: 'numberOfBonds', headerName: '# Bonds', width: 70 },
    {
      field: 'totalFees',
      headerName: 'Total Fees',
      width: 100,
      valueGetter: (value, row) => `$${calculateTotalFees(row).toFixed(2)}`,
    },
    {
      field: 'promisedPostingTimeInMinutes',
      headerName: 'Post Time (hrs)',
      width: 150,
      valueGetter: (value, row) => convertMinutesToHours(value),
    },
    {
      field: 'expireDate',
      headerName: 'Expires',
      width: 150,
      valueGetter: (value, row) => calculateCountdown(value),
    },
    {
      field: 'cancel',
      headerName: 'Cancel',
      width: 100,
      renderCell: (params) => (
        <IconButton color="secondary" onClick={() => handleCancel(params.row.transferBondQuoteID)}>
          <CloseIcon />
        </IconButton>
      ),
    }
  ];
  const declinedColumns = [
    { field: 'transferBondTransactionID', headerName: 'Ref #', width: 100 },
    { field: 'defendantName', headerName: 'Defendant Name', width: 200 },
    ...(canViewRequestingAgency
      ? [
          {
            field: 'requestingAgencyName',
            headerName: 'Requesting Agency',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  width: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                }}
              >
                {params.row.requestingAgencyName || '-'}
              </Box>
            ),
          },
        ]
      : []),
    { field: 'county', headerName: 'County', width: 150 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'declineReason', headerName: 'Decline Reason', width: 250},
    {
      field: 'totalFees',
      headerName: 'Total Fees',
      width: 150,
      valueGetter: (value, row) => `$${calculateTotalFees(row).toFixed(2)}`,
    },
    { field: 'dateRecorded', headerName: 'Date Given', width: 150 , valueGetter: (value, row) => `${formatDate(row)}`},

    {
      field: 'clear',
      headerName: 'Clear',
      width: 100,
      renderCell: (params) => (
        <IconButton color="secondary" onClick={() => handleClear(params.row.transferBondQuoteID)}>
          <CloseIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Paper sx={{ padding: 3, mb: 4 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Navigate between Accepted Quotes, Pending Quotes, and Declined Requests using the tabs below:
      </Typography>
      <Tabs
        value={activeTabQuotes}
        onChange={handleTabChangeQuotes}
        textColor="secondary"
        indicatorColor="secondary"
        aria-label="Quotes Tabs"
        variant="fullWidth"
        sx={{
          '& .Mui-selected': {
            backgroundColor: '#f9f9f9',
            fontWeight: 'bold',
          },
        }}
      >
        <Tab label={`Pending Quotes (${pendingQuotes.length})`} />
        <Tab label={`Declined and Expired Requests (${declinedQuotes.length})`} />
      </Tabs>

      {activeTabQuotes === 0 && (
        <Paper sx={{ mt: 2, width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Pending Quotes
          </Typography>
          <DataGrid
            rows={pendingQuotes}
            columns={quoteColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            autoHeight
            loading={loading}
            disableSelectionOnClick
            getRowId={(row) => row.transferBondTransactionID}
          />
        </Paper>
      )}

      {activeTabQuotes === 1 && (
        <Paper sx={{ mt: 2, width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Declined Requests
          </Typography>
          <DataGrid
            rows={declinedQuotes}
            columns={declinedColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            autoHeight
            loading={loading}
            disableSelectionOnClick
            getRowId={(row) => `${row.transferBondTransactionID}-${row.transferBondQuoteID || Math.random()}`} // Generate a unique ID

          />
        </Paper>
      )}
    </Paper>
  );
};

export default QuoteGrids;
